/* eslint-disable */
import { useEffect, useMemo, useContext, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ClientContext } from 'react-fetching-library';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { UserContext } from 'contexts';
import showServerError from 'helpers/showError';
import HipaaCheck from 'components/Shared/HipaaCheck';
import { checkPermission } from 'helpers/checkPermissions';
import permissions from 'config/permissions';
import { useSelector, useDispatch } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  logout as logoutAction,
  getMetadata,
  getUser,
  setUser as setUserAction,
  setHipaaCheckParams as setHipaaCheckParamsAction,
  setMetadata as setMetadataAction,
  setBlockedAccounts,
  setInitPath,
  getIsLogin,
  setIsLogin,
  setIsLoginStatus,
  getTransitionWithVariable,
  checkAuthorizationExpiresTimeout,
  REQUEST_STATUS,
  navigateToRoute,
} from '../redux_store';
import { matchPath } from 'react-router-dom';
import { ALL_PRIVATE_ROUTES, ALL_PUBLIC_ROUTES, ALL_ROUTES } from '../config/routes';

export const tryToParseRouteFromString = (path, routes) => {
  let parsedRoute;
  (routes || ALL_PRIVATE_ROUTES).forEach(i => {
    const match = matchPath({ path: i.path, end: true }, path);
    if (match) {
      parsedRoute = {
        path: i.path,
        params: {
          ...match.params,
        },
        title: i.additional.title,
      };
    }
  });
  return parsedRoute;
};

let metadata_store = null;

// Helper function to determine default route based on account type
const getDefaultRouteForAccount = (metadata, disabledFeatures = []) => {
  const accountPermissions = metadata?.currentAccountPermissions;

  // Check if populationHealth is accessible
  const populationHealthPermission = ALL_ROUTES.dashboard.populationHealth.additional?.permission;
  const populationHealthDisabledId = ALL_ROUTES.dashboard.populationHealth.additional?.disabledId;

  const hasPopulationHealthPermission = checkPermission(accountPermissions, populationHealthPermission);
  const isPopulationHealthEnabled = !disabledFeatures.includes(populationHealthDisabledId);
  const canAccessPopulationHealth = hasPopulationHealthPermission && isPopulationHealthEnabled;

  if (canAccessPopulationHealth) {
    return {
      path: ALL_ROUTES.dashboard.populationHealth.path,
      params: {
        currentAccountUuid: metadata.currentAccount.uuid,
      },
      title: ALL_ROUTES.dashboard.populationHealth.additional.title,
      clearBreadcrumbs: true,
    };
  }

  // Fallback: check if DS account
  const isDS = checkPermission(accountPermissions, permissions.ACCOUNTS_GET);
  if (isDS) {
    return {
      path: ALL_ROUTES.account.listDS.path,
      params: {
        currentAccountUuid: metadata.currentAccount.uuid,
      },
      title: ALL_ROUTES.account.listDS.additional.title,
      clearBreadcrumbs: true,
    };
  }

  // Default fallback: dashboard root
  return {
    path: ALL_ROUTES.dashboard.root.path,
    params: {
      currentAccountUuid: metadata.currentAccount.uuid,
    },
    title: ALL_ROUTES.dashboard.root.additional.title,
    clearBreadcrumbs: true,
  };
};

// Helper function to create route object with standard structure
const createRouteObject = (routeConfig, currentAccountUuid, additionalParams = {}) => {
  return {
    path: routeConfig.path,
    params: {
      currentAccountUuid,
      ...additionalParams,
    },
    title: routeConfig.additional?.title,
    clearBreadcrumbs: true,
    removeQueryString: true,
  };
};

const UserProvider = ({ children }) => {
  const { user, hipaaCheckParams, metadata, blockedAccounts, isLogin, status, initPath } =
    useSelector(state => state.user);
  const { disabledFeatures } = useSelector(state => state.appSettings);
  const dispatch = useDispatch();
  const clientContext = useContext(ClientContext);
  const {
    en: { notifications, errors },
  } = useSelector(state => state.i18n);
  const userUuid = user ? user.uuid : null;
  const [changeAccountLoading, setChangeAccountLoading] = useState(true);
  const OLD_PATH_FOR_SUPPORT_REDIRECT = {
    '/dashboard': ALL_ROUTES.dashboard.root,
    '/accounts/list': ALL_ROUTES.account.listDS,
    '/patients/list': ALL_ROUTES.patient.list,
    '/orders/list': ALL_ROUTES.order.list,
    '/assessments/list': ALL_ROUTES.assessment.list,
    '/resources/list': ALL_ROUTES.resource.list,
    '/claimsReporting': ALL_ROUTES.claim.list,
    '/claimReport/list': ALL_ROUTES.claimReport.list,
    '/reportables/list': ALL_ROUTES.reportable.list,
  };

  const setMetadata = newMetadata => {
    if (!isEqual(metadata, newMetadata)) {
      dispatch(setMetadataAction(newMetadata));
    }
  };

  useEffect(() => {
    metadata_store = metadata;
  }, [metadata]);

  // fetch metadata if user exist
  useEffect(() => {
    if (userUuid) {
      refreshMetadata();
    }
  }, [userUuid]);

  const refreshMetadata = () => {
    dispatch(getMetadata());
  };

  const checkHipaa = (newUser, newMetadata, newRoute) => {
    dispatch(
      setHipaaCheckParamsAction({
        newUser: newUser,
        newMetadata: newMetadata,
        newRoute: newRoute,
      })
    );
  };

  const changeAccount = async (accountUuid, route) => {
    setChangeAccountLoading(true);
    const { payload, error } = await clientContext.query({
      method: 'POST',
      endpoint: `/auth/token/account/${accountUuid}`,
    });
    if (!error && payload) {
      const { user: newUser, authMetadata } = payload;
      if (!authMetadata) {
        showServerError(dispatch, errors.doNotHaveAccessToAnyAccounts);
        return;
      }
      checkHipaa(newUser, authMetadata, route);
    } else {
      dispatch(
        enqueueSnackbar({
          message: notifications.noPermissions,
          options: {
            variant: SNACKBAR_VARIANTS.warning,
          },
        })
      );
      if (metadata?.currentAccount?.uuid) {
        dispatch(
          navigateToRoute({
            route: {
              clearBreadcrumbs: true,
              path: ALL_ROUTES.root.path,
              params: { currentAccountUuid: metadata?.currentAccount?.uuid },
              title: ALL_ROUTES.root.title || 'Dashboard',
            },
          })
        ).then(() => {
          setChangeAccountLoading(false);
        });
      } else {
        dispatch(
          navigateToRoute({
            route: {
              path: ALL_ROUTES.login.path,
              clearBreadcrumbs: true,
            },
          })
        ).then(() => {
          setChangeAccountLoading(false);
        });
      }
    }
  };

  const handleSuccessHipaaCheck = () => {
    const { newUser, newMetadata, newRoute } = hipaaCheckParams;
    dispatch(setUserAction(newUser));
    if (newMetadata === null || Object.keys(newMetadata).length === 0) {
      showServerError(dispatch, errors.doNotHaveAccessToAnyAccounts);
      return;
    }
    if (newRoute) {
      dispatch(setInitPath(newRoute));
    }
    // Force metadata update by dispatching directly to Redux
    dispatch(setMetadataAction({ ...newMetadata, route: newRoute }));

    // Check if we need to trigger redirect manually (if metadata already existed)
    const isMetadataAlreadyExists = metadata?.currentAccount?.uuid;

    if (isMetadataAlreadyExists) {
      const { pathname } = location;
      let finalNewRoute = newRoute || initPath;

      if (!finalNewRoute || finalNewRoute.isUnknown) {
        if (finalNewRoute) {
          const parsedRoute = tryToParseRouteFromString(finalNewRoute?.path, ALL_PUBLIC_ROUTES);
          const parsedOldRoute = OLD_PATH_FOR_SUPPORT_REDIRECT[finalNewRoute?.path];

          if (parsedOldRoute) {
            finalNewRoute = createRouteObject(parsedOldRoute, newMetadata.currentAccount.uuid);
          } else if (parsedRoute) {
            finalNewRoute = getDefaultRouteForAccount(newMetadata, disabledFeatures);
          } else {
            finalNewRoute = {
              path: finalNewRoute?.path || pathname,
            };
          }
        } else {
          finalNewRoute = getDefaultRouteForAccount(newMetadata, disabledFeatures);
        }
      }

      const manualRedirectRoute = {
        clearBreadcrumbs: true,
        path: finalNewRoute.path,
        params: { currentAccountUuid: newMetadata?.currentAccount?.uuid, ...finalNewRoute.params },
        title: finalNewRoute.title,
      };

      dispatch(
        navigateToRoute({
          route: manualRedirectRoute,
        })
      )
        .then(() => {
          setChangeAccountLoading(false);
        })
        .catch(error => {
          setChangeAccountLoading(false);
        });

      dispatch(setInitPath(null));
    }

    dispatch(setHipaaCheckParamsAction(null));
    dispatch(setIsLogin(true));
    dispatch(setIsLoginStatus(REQUEST_STATUS.succeeded));
  };

  const handleFailHipaaCheck = () => {
    dispatch(logoutAction());
  };

  const setUser = newUser => {
    dispatch(setUserAction(newUser));
  };

  const handleSwitchHipaaAccount = useCallback(() => {
    const {
      newMetadata: { accounts, currentAccount },
    } = hipaaCheckParams;
    dispatch(setHipaaCheckParamsAction(null));

    const newBlockedAccounts = uniq([...blockedAccounts, currentAccount.uuid]);
    dispatch(setBlockedAccounts(newBlockedAccounts));
    const accountsToCheck = accounts.filter(item => !newBlockedAccounts.includes(item.uuid));

    if (accountsToCheck.length) {
      changeAccount(accountsToCheck[0].uuid);
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.youHaveBeenSwitched, {
            name: accountsToCheck[0].businessName,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        })
      );
      dispatch(setInitPath(null));
    } else {
      dispatch(logoutAction());
    }
  }, [blockedAccounts, hipaaCheckParams]);

  useEffect(() => {
    if (metadata?.currentAccount?.uuid) {
      const { pathname } = location;

      // Don't redirect if user is on password reset page (completing password reset flow)
      if (pathname === '/password/reset' || pathname.startsWith('/password/reset')) {
        setChangeAccountLoading(false);
        return;
      }

      if (
        initPath &&
        initPath.params?.currentAccountUuid &&
        initPath.params?.currentAccountUuid !== metadata?.currentAccount?.uuid
      ) {
        setChangeAccountLoading(true);
        changeAccount(initPath.params.currentAccountUuid, initPath);
      } else {
        let newRoute = initPath;
        if (!initPath || initPath.isUnknown) {
          if (initPath) {
            const parsedRoute = tryToParseRouteFromString(initPath?.path, ALL_PUBLIC_ROUTES);
            const parsedOldRoute = OLD_PATH_FOR_SUPPORT_REDIRECT[initPath?.path];

            if (parsedOldRoute) {
              newRoute = createRouteObject(parsedOldRoute, metadata.currentAccount.uuid);
            } else if (parsedRoute) {
              newRoute = getDefaultRouteForAccount(metadata, disabledFeatures);
            } else {
              newRoute = {
                path: initPath?.path || pathname,
              };
            }
          } else {
            newRoute = getDefaultRouteForAccount(metadata, disabledFeatures);
          }
        }

        const finalRoute = {
          clearBreadcrumbs: true,
          path: newRoute.path,
          params: { currentAccountUuid: metadata?.currentAccount?.uuid, ...newRoute.params },
          title: newRoute.title,
        };

        dispatch(
          navigateToRoute({
            route: finalRoute,
          })
        )
          .then(() => {
            setChangeAccountLoading(false);
          })
          .catch(error => {
            setChangeAccountLoading(false);
          });
        dispatch(setInitPath(null));
      }
    }
  }, [metadata?.currentAccount?.uuid]);

  useEffect(() => {
    dispatch(getIsLogin());
    dispatch(checkAuthorizationExpiresTimeout());

    const { pathname } = location;
    const parsedRoute = tryToParseRouteFromString(pathname);

    if (parsedRoute) {
      dispatch(setInitPath(parsedRoute));
    } else {
      const parsedPublicRoute = tryToParseRouteFromString(pathname, ALL_PUBLIC_ROUTES);

      if (!parsedPublicRoute) {
        dispatch(
          setInitPath({
            path: pathname,
            isUnknown: true,
          })
        );
      }
      setChangeAccountLoading(false);
    }

    if (isLogin) {
      dispatch(getUser());
    }
  }, []);

  useEffect(() => {
    if (isLogin && !user) {
      dispatch(getUser());
    }
    if (isLogin === false) {
      const { pathname } = location;
      const parsedRoute = tryToParseRouteFromString(pathname);
      if (!!parsedRoute) {
        dispatch(
          navigateToRoute({
            route: {
              path: ALL_ROUTES.login.path,
              clearBreadcrumbs: true,
            },
          })
        ).then(() => {
          setChangeAccountLoading(false);
        });
      }
    }
  }, [isLogin]);

  useEffect(() => {
    if (status === REQUEST_STATUS.failed) {
      dispatch(
        navigateToRoute({
          route: {
            path: ALL_ROUTES.login.path,
            clearBreadcrumbs: true,
          },
        })
      ).then(() => {
        setChangeAccountLoading(false);
      });
    }
  }, [status]);

  const eventListener = () => {
    /*
         handle changing route outside the dashboard
         case: browser back button click
       */
    let newRoute = tryToParseRouteFromString(document.location.pathname);
    const accountUuid =
      metadata_store && metadata_store.currentAccount ? metadata_store.currentAccount.uuid : null;

    if (
      newRoute?.params?.currentAccountUuid &&
      newRoute.params.currentAccountUuid !== accountUuid
    ) {
      setChangeAccountLoading(true);
      changeAccount(newRoute.params.currentAccountUuid, newRoute);
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', eventListener, true);
    return () => {
      window.removeEventListener('popstate', eventListener);
    };
  }, []);

  // memoize a provider value
  const value = useMemo(
    () => ({
      changeAccount,
      checkHipaa,
      handleFailHipaaCheck,
      setUser,
      changeAccountLoading,
    }),
    [user, metadata, changeAccount, changeAccountLoading]
  );

  return (
    <UserContext.Provider value={value}>
      {children}
      {hipaaCheckParams && (
        <HipaaCheck
          user={hipaaCheckParams.newUser}
          metadata={hipaaCheckParams.newMetadata}
          onSuccess={handleSuccessHipaaCheck}
          onFail={handleFailHipaaCheck}
          onSwitchAccount={handleSwitchHipaaAccount}
        />
      )}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default UserProvider;
